<template>
  <!-- How a client or partition sends email notifications: one mode, then which email types (US 9391) -->
  <div class="cp-stack" qid="client-email-settings">
    <div class="v2-card">
      <div class="v2-card__head">
        <h3>{{ $t('client_page.notifications.title') }}
          <span class="v2-card__sub">· {{ $t('client_page.notifications.for_everyone', { name: client.name }) }}</span>
        </h3>
      </div>
      <div class="v2-card__body">
        <div class="cp-label">{{ $t('client_page.notifications.send_emails') }}</div>
        <div class="cp-modes" role="radiogroup">
          <button v-for="mode in modes"
                  :key="mode"
                  type="button"
                  role="radio"
                  class="cp-mode"
                  :class="{ 'is-on': client.email_mode === mode }"
                  :aria-checked="client.email_mode === mode ? 'true' : 'false'"
                  :disabled="!canEdit || saving"
                  :qid="'client-email-mode-' + mode"
                  @click="save({ email_mode: mode })">
            <span class="cp-mode__title">{{ $t('client_page.notifications.mode.' + mode) }}</span>
            <span class="cp-mode__text">{{ $t('client_page.notifications.mode_text.' + mode) }}</span>
          </button>
        </div>

        <div class="cp-label cp-label--spaced">{{ $t('client_page.notifications.include') }}</div>
        <div class="cp-switches">
          <div v-for="type in types"
               :key="type"
               class="cp-switch"
               :class="{ 'is-disabled': client.email_mode === 'off' }">
            <div>
              <div class="cp-switch__title">{{ $t('client_page.notifications.type.' + type) }}</div>
              <div class="cp-switch__text">{{ $t('client_page.notifications.type_text.' + type) }}</div>
            </div>
            <b-form-checkbox switch
                             size="lg"
                             :checked="!!client[type]"
                             :disabled="!canEdit || saving || client.email_mode === 'off'"
                             :aria-label="$t('client_page.notifications.type.' + type)"
                             :qid="'client-email-' + type"
                             @change="value => save({ [type]: value ? 1 : 0 })"/>
          </div>
        </div>
      </div>
    </div>

    <div class="us-note us-note--info" qid="client-email-next">
      <font-awesome-icon icon="info-circle"/>
      <span>{{ nextText }} {{ $t('client_page.notifications.saved_and_logged') }}</span>
    </div>
  </div>
</template>

<script>
  import piincHttp from '@/util/configureAxios.js';
  import moment from 'moment';

  const TYPES = ['email_task_assigned', 'email_task_due_soon', 'email_task_overdue', 'email_audit_plan_read'];

  export default {
    name: 'ClientEmailSettings',
    props: {
      client: Object,
      canEdit: Boolean
    },
    data() {
      return {
        modes: ['off', 'instant', 'daily', 'weekly'],
        types: TYPES,
        saving: false
      }
    },
    computed: {
      // When the next summary goes out: 07:00 on the next weekday, or next Monday
      nextText() {
        let mode = this.client.email_mode;
        if (mode !== 'daily' && mode !== 'weekly') {
          return this.$t('client_page.notifications.next.' + (mode || 'off'));
        }

        let next = moment().hour(7).minute(0).second(0);
        if (mode === 'daily') {
          while (next.isBefore(moment()) || next.isoWeekday() > 5) {
            next.add(1, 'day');
          }
        } else {
          while (next.isBefore(moment()) || next.isoWeekday() !== 1) {
            next.add(1, 'day');
          }
        }

        let day = next.isSame(moment(), 'day') ? this.$t('profile_page.today').toLowerCase()
          : next.isSame(moment().add(1, 'day'), 'day') ? this.$t('client_page.notifications.tomorrow').toLowerCase()
            : next.format('dddd D MMMM');

        return this.$t('client_page.notifications.next.' + mode, { day: day, time: next.format('HH:mm') });
      }
    },
    methods: {
      save(changes) {
        let old = {};
        Object.keys(changes).forEach(key => {
          old[key] = this.client[key];
          this.$set(this.client, key, changes[key]);
        });

        this.saving = true;
        piincHttp.put('clients/' + this.client.slug, changes).then(response => {
          this.saving = false;
          ['email_mode'].concat(TYPES).forEach(key => this.$set(this.client, key, response.data[key]));
        }, () => {
          // Put the switch back when the change wasn't saved
          Object.keys(old).forEach(key => this.$set(this.client, key, old[key]));
          this.saving = false;
          this.$toastr('error', this.$t('client_page.notifications.error'));
        });
      }
    }
  }
</script>
